import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PlusCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import { entriesApi } from '../../../api/entries';
import { projectsApi } from '../../../api/projects';
import { useCurrentUser } from '../../../contexts/CurrentUserContext';
import { Project } from '../../../types';
import { getWeekNumber, isWeekendDate } from '../../../lib/utils';
import { Button } from '../../../components/ui/atoms/button';
import { Input } from '../../../components/ui/atoms/input';
import {
  quickHoursToRangeFrom,
  rangeToHours,
  isHalfHourStep,
  HALF_HOUR_ERROR,
  rangesOverlap,
  getNextStartTime,
  getDailyHoursWarning,
  futureDateError,
  routesToDirectManager,
  DEFAULT_START_HOUR,
  MIN_DAILY_HOURS,
  OVERLAP_ERROR_MESSAGE,
} from '../../../lib/timeEntryHelpers';
import { AmbiguousWordRule, DayCell, EntryMode, EntryRow, ExistingRange, EntryToCreate } from './interface';
import {
  getWeekDates, formatWeekRange, newRow, getCell, effectiveHours,
  containsInternalMeeting, INTERNAL_MEETING_ERROR, AMBIGUOUS_WORD_RULES,
  DRAFT_KEY, loadDraft, hasDraftContent,
} from './utils';
import { ModeToggle, WordConfirmDialog } from './components';
import { TimesheetTable } from './partials';

export function NewEntryPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const todayStr = new Date().toISOString().slice(0, 10);

  // Draft left over from a refresh/closed tab, read once on mount.
  const [draft] = useState(loadDraft);
  const [draftRestored, setDraftRestored] = useState(!!draft);

  const [refDate, setRefDate] = useState(draft?.refDate ?? todayStr);

  const year = parseInt(refDate.slice(0, 4), 10);
  const week = getWeekNumber(refDate);
  const weekDates = useMemo(() => getWeekDates(year, week), [year, week]);

  const [mode, setMode] = useState<EntryMode>(draft?.mode ?? 'quick');
  const [rows, setRows] = useState<EntryRow[]>(draft?.rows ?? [newRow()]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  // Holds a validated, ready-to-send batch while we wait on the ambiguous-word
  // confirmation dialog. Null whenever no confirmation is pending.
  const [pendingSubmission, setPendingSubmission] = useState<EntryToCreate[] | null>(null);
  const [activeWordRule, setActiveWordRule] = useState<AmbiguousWordRule | null>(null);

  const { data: projectsData } = useQuery({
    queryKey: ['projects', 'mine'],
    queryFn: () => projectsApi.list({ mine: true }).then((r) => r.data),
  });
  const projects: Project[] = useMemo(
    () => (projectsData ?? []).filter((p) => p.status !== 'FROZEN' && p.status !== 'CLOSED'),
    [projectsData],
  );

  // Logged-in user's own profile — used to default each entry's job title
  const { user: me } = useCurrentUser();
  const defaultJobTitleId = me?.jobTitleId ?? '';

  // Existing entries already saved this week (so the 8h/day check, sequential
  // start times, and overlap check all account for them too)
  const { data: existingWeekEntries } = useQuery({
    queryKey: ['my-entries-week', weekDates[0], weekDates[6]],
    queryFn: () =>
      entriesApi.listMine({ from: weekDates[0], to: weekDates[6], limit: 200 }).then((r) => r.data?.data ?? []),
  });

  // Normalizes existingWeekEntries' fromHour/toHour (which may come back as
  // full ISO timestamps from the API) down to 'HH:MM' for use with the helpers.
  const existingRanges: ExistingRange[] = useMemo(
    () =>
      (existingWeekEntries ?? []).map((e: any) => ({
        id: e.id,
        date: e.date?.slice(0, 10) ?? '',
        fromHour: e.fromHour?.includes?.('T') ? e.fromHour.slice(11, 16) : e.fromHour,
        toHour: e.toHour?.includes?.('T') ? e.toHour.slice(11, 16) : e.toHour,
      })),
    [existingWeekEntries]
  );

  // Persist the in-progress grid on every change so a refresh doesn't lose it.
  // Cleared once the grid is empty again, or on a successful save.
  useEffect(() => {
    if (hasDraftContent(rows)) {
      localStorage.setItem(DRAFT_KEY, JSON.stringify({ refDate, mode, rows }));
    } else {
      localStorage.removeItem(DRAFT_KEY);
    }
  }, [refDate, mode, rows]);

  const discardDraft = () => {
    localStorage.removeItem(DRAFT_KEY);
    setRows([newRow()]);
    setRefDate(todayStr);
    setMode('quick');
    setErrors([]);
    setDraftRestored(false);
  };

  const shiftWeek = (delta: number) => {
    const d = new Date(refDate + 'T12:00:00Z');
    d.setUTCDate(d.getUTCDate() + delta * 7);
    setRefDate(d.toISOString().slice(0, 10));
  };

  const setRowProject = (rowId: string, projectId: string) =>
    setRows((r) => r.map((row) => (row.rowId === rowId ? { ...row, projectId, phaseId: '' } : row)));
  const setRowPhase = (rowId: string, phaseId: string) =>
    setRows((r) => r.map((row) => (row.rowId === rowId ? { ...row, phaseId } : row)));
  const setCell = (rowId: string, date: string, field: keyof DayCell, value: string) =>
    setRows((r) =>
      r.map((row) => {
        if (row.rowId !== rowId) return row;
        const cell = getCell(row, date);
        return { ...row, days: { ...row.days, [date]: { ...cell, [field]: value } } };
      })
    );

  // ── Per-day totals across all rows (for the warning banners + footer) ──
  const dayTotals = useMemo(() => {
    const totals: Record<string, number> = {};
    for (const date of weekDates) {
      const fromExisting = (existingWeekEntries ?? [])
        .filter((e: any) => e.date.slice(0, 10) === date)
        .reduce((s: number, e: any) => s + e.hours, 0);
      const fromGrid = rows.reduce((s, row) => s + effectiveHours(getCell(row, date), mode), 0);
      totals[date] = fromExisting + fromGrid;
    }
    return totals;
  }, [weekDates, existingWeekEntries, rows, mode, defaultJobTitleId]);

  // Builds the batch of entries to submit and runs all validations, including
  // the note guardrails. Returns both so the caller decides
  // what to do next (show errors, ask for training confirmation, or submit).
  const buildAndValidate = (): { toCreate: EntryToCreate[]; errs: string[] } => {
    const errs: string[] = [];
    const toCreate: EntryToCreate[] = [];

    // Tracks, per date, the running "next start time" as rows are processed in
    // order (Quick Hours mode) plus all ranges already queued this submission
    // (both modes), so overlap checks see entries added earlier in this same loop.
    const cursorByDate: Record<string, string> = {};
    const queuedByDate: Record<string, Array<{ fromHour: string; toHour: string }>> = {};
    for (const date of weekDates) {
      cursorByDate[date] = getNextStartTime(existingRanges, date);
      queuedByDate[date] = existingRanges.filter((e) => e.date === date);
    }

    rows.forEach((row, i) => {
      const label = `Row ${i + 1}`;
      // Non-project time (leave, admin, internal meetings, learning) is the one
      // kind exempt from the future-date limit and the note guardrails.
      const isNonProjectRelated = routesToDirectManager(row.projectId);
      let rowHasHours = false;

      weekDates.forEach((date) => {
        const cell = getCell(row, date);
        const h = effectiveHours(cell, mode);
        if (h <= 0) return;

        if (!isHalfHourStep(h)) {
          errs.push(`${label} (${date}): ${HALF_HOUR_ERROR}`);
          return;
        }

        if (isWeekendDate(date)) {
          errs.push(`${label}: hours cannot be logged on Friday or Saturday`);
          return;
        }

        rowHasHours = true;
        if (!row.projectId) { errs.push(`${label}: select a project`); return; }

        // Future dates: unrestricted for non-project time, current week only
        // for anything logged against a real project.
        const futureError = futureDateError(date, isNonProjectRelated);
        if (futureError) { errs.push(`${label}: ${futureError}`); return; }

        if (!row.phaseId) { errs.push(`${label}: select a phase`); return; }
        if (!cell.notes || !cell.notes.trim()) { errs.push(`${label}: notes are required for ${date}`); return; }

        // "internal meeting" (in any spacing/punctuation variant) belongs to
        // non-project time — it's never a valid entry against a real project.
        if (!isNonProjectRelated && containsInternalMeeting(cell.notes)) {
          errs.push(`${label} (${date}): ${INTERNAL_MEETING_ERROR}`);
          return;
        }

        let fromHour: string, toHour: string;
        if (mode === 'quick') {
          // Start where the previous task on this date left off, not always at 09:00.
          const range = quickHoursToRangeFrom(h, cursorByDate[date]);
          fromHour = range.fromHour;
          toHour = range.toHour;
        } else {
          fromHour = cell.fromHour;
          toHour = cell.toHour;
        }

        // Overlap check against existing entries and anything already queued
        // for this date earlier in this same submission.
        const conflict = queuedByDate[date].some((q) => rangesOverlap(fromHour, toHour, q.fromHour, q.toHour));
        if (conflict) {
          errs.push(`${label} (${date}): ${OVERLAP_ERROR_MESSAGE}`);
          return;
        }

        toCreate.push({
          projectId: row.projectId,
          phaseId: row.phaseId,
          jobTitleId: defaultJobTitleId,
          date,
          fromHour,
          toHour,
          notes: cell.notes,
          isNonProjectRelated,
        });

        queuedByDate[date].push({ fromHour, toHour });
        // Advance the cursor for quick mode so the next row on this date stacks after this one.
        if (mode === 'quick') {
          cursorByDate[date] = toHour;
        }
      });

      if (rowHasHours && !row.projectId) errs.push(`${label}: select a project`);
      else if (rowHasHours && !row.phaseId) errs.push(`${label}: select a phase`);
    });

    // Block submission if any day with hours entered falls outside the daily range
    // (existing non-rejected entries for that day + everything queued in this submission).
    for (const date of weekDates) {
      const existingTotal = (existingWeekEntries ?? [])
        .filter((e: any) => e.date.slice(0, 10) === date && e.status !== 'REJECTED')
        .reduce((s: number, e: any) => s + e.hours, 0);
      const gridTotal = toCreate
        .filter((e) => e.date === date)
        .reduce((s, e) => s + rangeToHours(e.fromHour, e.toHour), 0);
      if (gridTotal <= 0) continue;
      const warning = getDailyHoursWarning(existingTotal, gridTotal);
      if (warning) errs.push(`${date}: ${warning}`);
    }

    return { toCreate, errs: errs.filter((e, i) => errs.indexOf(e) === i) };
  };

  const submitEntries = async (toCreate: EntryToCreate[]) => {
    setIsSubmitting(true);
    try {
      // isNonProjectRelated is only used for front-end validation — strip it before hitting the API.
      await entriesApi.batchCreate(toCreate.map(({ isNonProjectRelated, ...e }) => e));
      localStorage.removeItem(DRAFT_KEY);
      queryClient.invalidateQueries({ queryKey: ['my-entries'] });
      queryClient.invalidateQueries({ queryKey: ['approvals-queue-count'] });
      queryClient.invalidateQueries({ queryKey: ['my-calendar'] });
      navigate('/employee/entries');
    } catch (e: any) {
      setErrors([e?.response?.data?.error || e?.response?.data?.message || 'Failed to save entries. Please try again.']);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    setErrors([]);
    const { toCreate, errs } = buildAndValidate();

    if (errs.length > 0) { setErrors(errs); return; }
    if (toCreate.length === 0) { setErrors(['No hours entered. Fill in at least one day.']); return; }

    // If any project entry's notes match an ambiguous word (training,
    // learning, ...), pause and ask once for the whole batch before calling
    // the API. Non-project time is exempt — that's where personal training and
    // learning legitimately belong. Rules are checked in order; first match wins.
    const matchedRule = AMBIGUOUS_WORD_RULES.find((rule) =>
      toCreate.some((e) => !e.isNonProjectRelated && rule.detect(e.notes))
    );
    if (matchedRule) {
      setPendingSubmission(toCreate);
      setActiveWordRule(matchedRule);
      return;
    }

    await submitEntries(toCreate);
  };

  const handleLegitimateConfirm = () => {
    setActiveWordRule(null);
    const toCreate = pendingSubmission;
    setPendingSubmission(null);
    if (toCreate) submitEntries(toCreate);
  };

  const handlePersonalConfirm = () => {
    const rule = activeWordRule;
    setActiveWordRule(null);
    setPendingSubmission(null);
    if (rule) setErrors([rule.personalError]);
  };

  const totalHours = Object.values(dayTotals).reduce((s, h) => s + h, 0);
  const anyDayUnderMinimum = weekDates.some((d) => dayTotals[d] > 0 && dayTotals[d] < MIN_DAILY_HOURS);

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Log Hours</h1>
          <p className="text-sm text-muted-foreground">Record your time for the week</p>
        </div>
        <Button variant="outline" onClick={() => navigate('/employee/entries')}>Cancel</Button>
      </div>

      {/* Week selector + mode toggle */}
      <div className="flex flex-wrap justify-between items-center gap-4 rounded-lg border bg-card px-4 py-3">
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => shiftWeek(-1)}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="min-w-[200px] text-center">
            <div className="font-semibold">Week {week}, {year}</div>
            <div className="text-xs text-muted-foreground">{formatWeekRange(weekDates)}</div>
          </div>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => shiftWeek(1)}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex flex-wrap items-center gap-4">

          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <label htmlFor="jump-date" className="whitespace-nowrap">Jump to date:</label>
            <Input id="jump-date" type="date" value={refDate} onChange={(e) => setRefDate(e.target.value)} className="h-8 w-36 text-sm" />
          </div>

          <ModeToggle mode={mode} onChange={setMode} />
        </div>

      </div>


      {mode === 'quick' && (
        <p className="text-xs text-muted-foreground -mt-2">
          Quick Hours starts each entry at {DEFAULT_START_HOUR}, or right after the previous task on that day if one exists. End time is calculated automatically.
        </p>
      )}

      {/* Restored-draft notice */}
      {draftRestored && (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800 dark:border-blue-900 dark:bg-blue-950/30 dark:text-blue-400">
          <span>Restored your unsaved hours from last time. They aren't submitted yet.</span>
          <Button variant="ghost" size="sm" onClick={discardDraft}>Discard</Button>
        </div>
      )}

      {/* Per-day under-minimum warning banner */}
      {anyDayUnderMinimum && (
        <div className="rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 px-3 py-2 text-sm text-amber-800 dark:text-amber-400">
          Some days have less than {MIN_DAILY_HOURS}h logged. You won't be able to save until each day with hours reaches at least {MIN_DAILY_HOURS}h.
        </div>
      )}

      {/* Timesheet table */}
      <TimesheetTable
        weekDates={weekDates}
        mode={mode}
        rows={rows}
        projects={projects}
        dayTotals={dayTotals}
        totalHours={totalHours}
        onSetRowProject={setRowProject}
        onSetRowPhase={setRowPhase}
        onSetCell={setCell}
        onRemoveRow={(rowId) => setRows((r) => r.filter((x) => x.rowId !== rowId))}
      />

      {/* Footer */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button variant="outline" size="sm" onClick={() => setRows((r) => [...r, newRow()])}>
          <PlusCircle className="mr-2 h-4 w-4" />
          Add Project Row
        </Button>

        <div className="flex items-center gap-3">
          {errors.length > 0 && (
            <div className="text-sm text-destructive">
              {errors.map((e, i) => <p key={i}>{e}</p>)}
            </div>
          )}
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? 'Saving…' : 'Save Week'}
          </Button>
        </div>
      </div>

      <WordConfirmDialog
        rule={activeWordRule}
        onConfirmLegitimate={handleLegitimateConfirm}
        onConfirmPersonal={handlePersonalConfirm}
      />
    </div>
  );
}
